import "./styles.css";
import { AudioSystem } from "./client/audio";
import { PATENT_IMAGE_URLS } from "./client/patent-assets";
import { drawTomatoBody, Renderer } from "./client/renderer";
import { SKINS, isSkinUnlocked, newlyUnlockedSkins } from "./client/skins";
import { loadSave, recordBest, recordRun, save, type Settings } from "./client/storage";
import { FIXED_DT, MAX_PATENTS, type GameEvent, type PowerupKind, type RunMode } from "./core/constants";
import { Game, initializePhysics } from "./core/game";

await initializePhysics();
import { MAIN_TIMELINE_SEED, randomSeed } from "./core/random";

function requiredElement<T extends Element>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing required element #${id}`);
  return element as unknown as T;
}
for (const image of document.querySelectorAll<HTMLImageElement>("img[data-patent-kind]")) {
  const kind = image.dataset.patentKind as PowerupKind;
  image.src = PATENT_IMAGE_URLS[kind];
}

const canvas = requiredElement<HTMLCanvasElement>("game");
const renderer = new Renderer(canvas);
const audio = new AudioSystem();
const saveData = loadSave();
const game = new Game(MAIN_TIMELINE_SEED, "normal", randomSeed());

const scoreElement = requiredElement<HTMLElement>("score");
const bestScoreElement = requiredElement<HTMLElement>("best-score");
const hudBest = requiredElement<HTMLElement>("hud-best");
const newBestBanner = requiredElement<HTMLElement>("new-best-banner");
const skinUnlockNotice = requiredElement<HTMLElement>("skin-unlock-notice");
const skinPreview = requiredElement<HTMLCanvasElement>("skin-preview");
const skinPicker = requiredElement<HTMLElement>("skin-picker");
const skinName = requiredElement<HTMLElement>("skin-name");
const skinLock = requiredElement<HTMLElement>("skin-lock");
let previewSkinIndex = SKINS.findIndex(skin => skin.id === saveData.settings.skin);

const newBestStamp = requiredElement<HTMLElement>("new-best-stamp");
const patentElement = requiredElement<HTMLElement>("patents");
const patentMultiplierElement = requiredElement<HTMLElement>("patent-multiplier");
const depthElement = requiredElement<HTMLElement>("depth");
const eraStatusElement = requiredElement<HTMLElement>("era-status");
const titlePanel = requiredElement<HTMLElement>("title-panel");
const gameOverPanel = requiredElement<HTMLElement>("gameover-panel");
const pausePanel = requiredElement<HTMLElement>("pause-panel");
const pauseButton = requiredElement<HTMLButtonElement>("pause-button");
const settingsButton = requiredElement<HTMLButtonElement>("settings-button");
const resultScore = requiredElement<HTMLElement>("result-score");
const resultDepth = requiredElement<HTMLElement>("result-depth");
const resultBest = requiredElement<HTMLElement>("result-best");
const deathReason = requiredElement<HTMLElement>("death-reason");
const bestTitle = requiredElement<HTMLElement>("best-title");
const alternateButton = requiredElement<HTMLButtonElement>("alternate-button");
const announcement = requiredElement<HTMLElement>("announcement");
const settingsDialog = requiredElement<HTMLDialogElement>("settings-dialog");
const muteSetting = requiredElement<HTMLInputElement>("mute-setting");
const motionSetting = requiredElement<HTMLInputElement>("motion-setting");
const contrastSetting = requiredElement<HTMLInputElement>("contrast-setting");
const toggleSetting = requiredElement<HTMLInputElement>("toggle-setting");
const invertSetting = requiredElement<HTMLInputElement>("invert-setting");

let physicalHeld = false;
let toggledCore = false;
let gamepadHeld = false;
let gamepadNeedsRelease = true;
let accumulator = 0;
let lastFrameTime = performance.now();
let settingsPausedGame = false;
let recordedDeathTick = -1;
let runStartBest = saveData.bestScore;
let newBestNotified = false;

function currentSettings(): Settings {
  return saveData.settings;
}

function applySettings(): void {
  const settings = currentSettings();
  muteSetting.checked = settings.muted;
  motionSetting.checked = settings.reducedMotion;
  contrastSetting.checked = settings.highContrast;
  toggleSetting.checked = settings.toggleControl;
  invertSetting.checked = settings.invertHold;
  renderer.setPreferences({ skin: settings.skin, reducedMotion: settings.reducedMotion, highContrast: settings.highContrast });
  updateSkinPreview();
  audio.setMuted(settings.muted);
  canvas.closest("#game-shell")?.classList.toggle("reduced-motion", settings.reducedMotion);
}

function updateSkinPreview(): void {
  const skin = SKINS[previewSkinIndex]!;
  const unlocked = isSkinUnlocked(skin.id, saveData.bestScore);
  skinName.textContent = skin.name;
  skinLock.textContent = unlocked ? "" : `LOCKED — BEST ${skin.unlockBest.toLocaleString()}`;
  skinPicker.classList.toggle("is-locked", !unlocked);
  const ctx = skinPreview.getContext("2d");
  if (ctx) {
    ctx.clearRect(0, 0, 48, 48);
    ctx.save();
    ctx.translate(24, 29);
    ctx.scale(0.72, 0.72);
    drawTomatoBody(ctx, skin.palette, "pulp", false, 20, currentSettings().highContrast);
    ctx.restore();
  }
}

function cycleSkin(direction: number): void {
  previewSkinIndex = (previewSkinIndex + direction + SKINS.length) % SKINS.length;
  const skin = SKINS[previewSkinIndex]!;
  if (isSkinUnlocked(skin.id, saveData.bestScore)) {
    saveData.settings.skin = skin.id;
    save(saveData);
    applySettings();
  } else updateSkinPreview();
}

requiredElement<HTMLButtonElement>("previous-skin").addEventListener("click", () => cycleSkin(-1));
requiredElement<HTMLButtonElement>("next-skin").addEventListener("click", () => cycleSkin(1));

function effectiveCore(): boolean {
  const requested = currentSettings().toggleControl ? toggledCore : physicalHeld || gamepadHeld;
  return currentSettings().invertHold ? !requested : requested;
}

function updateRequestedMaterial(): void {
  game.setCoreRequested(effectiveCore());
}

function resetFrameClock(): void {
  accumulator = 0;
  lastFrameTime = performance.now();
}

function clearHeldInput(): void {
  physicalHeld = false;
  toggledCore = false;
  gamepadHeld = false;
  gamepadNeedsRelease = true;
  updateRequestedMaterial();
}

function updateNavigation(): void {
  pauseButton.classList.toggle("hidden", game.state !== "playing");
}

function pauseRun(): void {
  if (game.state !== "playing") return;
  game.pause();
  clearHeldInput();
  resetFrameClock();
  pausePanel.classList.remove("hidden");
  updateNavigation();
  announcement.textContent = "Time paused";
}

function resumeRun(): void {
  if (game.state !== "paused" || settingsDialog.open) return;
  game.resume();
  pausePanel.classList.add("hidden");
  resetFrameClock();
  updateNavigation();
  announcement.textContent = "Run resumed";
}

function beginRun(seed: number, mode: RunMode, eraSeed: number): void {
  runStartBest = saveData.bestScore;
  newBestNotified = false;
  newBestBanner.classList.add("hidden");
  newBestStamp.classList.add("hidden");
  skinUnlockNotice.classList.add("hidden");
  hudBest.classList.remove("is-new-best");
  clearHeldInput();
  settingsPausedGame = false;
  resetFrameClock();
  recordedDeathTick = -1;
  game.reset(seed, mode, "playing", eraSeed);
  titlePanel.classList.add("hidden");
  gameOverPanel.classList.add("hidden");
  pausePanel.classList.add("hidden");
  updateRequestedMaterial();
  updateNavigation();
  updateHud();
  announcement.textContent = mode === "random" ? "Random Timeline started" : "Main Timeline started";
}

function returnHome(): void {
  previewSkinIndex = SKINS.findIndex(skin => skin.id === saveData.settings.skin);
  updateSkinPreview();
  clearHeldInput();
  settingsPausedGame = false;
  resetFrameClock();
  recordedDeathTick = -1;
  game.reset(MAIN_TIMELINE_SEED, "normal", "title");
  titlePanel.classList.remove("hidden");
  gameOverPanel.classList.add("hidden");
  pausePanel.classList.add("hidden");
  updateRequestedMaterial();
  updateNavigation();
  bestTitle.textContent = saveData.bestScore.toLocaleString();
  announcement.textContent = "Main menu";
}

function handlePressStart(): void {
  void audio.unlock();
  if (game.state === "dead") {
    beginRun(game.seed, game.snapshot().mode, game.eraSeed);
  }
  if (game.state !== "playing") return;
  if (currentSettings().toggleControl) toggledCore = !toggledCore;
  else physicalHeld = true;
  updateRequestedMaterial();
}

function handlePressEnd(): void {
  if (game.state !== "playing" || currentSettings().toggleControl) return;
  physicalHeld = false;
  updateRequestedMaterial();
}

function showGameOver(events: GameEvent[]): void {
  const snapshot = game.snapshot();
  if (recordedDeathTick === snapshot.tick) return;
  recordedDeathTick = snapshot.tick;
  const milestones = recordRun(saveData, snapshot.score, snapshot.depth, snapshot.mode === "random");
  newBestStamp.classList.toggle("hidden", !(runStartBest > 0 && snapshot.score > runStartBest));
  const unlocked = newlyUnlockedSkins(runStartBest, saveData.bestScore);
  skinUnlockNotice.textContent = unlocked.length ? `SKIN UNLOCKED: ${unlocked.map(skin => skin.name).join(", ")}` : "";
  skinUnlockNotice.classList.toggle("hidden", unlocked.length === 0);
  resultScore.textContent = snapshot.score.toLocaleString();
  resultDepth.textContent = `${Math.floor(snapshot.depth / 40)}D`;
  resultBest.textContent = saveData.bestScore.toLocaleString();
  deathReason.textContent = game.reason;
  bestTitle.textContent = saveData.bestScore.toLocaleString();
  alternateButton.textContent = snapshot.mode === "random" ? "MAIN TIMELINE" : "NEW RANDOM TIMELINE";
  gameOverPanel.classList.remove("hidden");
  pausePanel.classList.add("hidden");
  clearHeldInput();
  updateNavigation();

  const death = events.find((event) => event.type === "death");
  announcement.textContent = milestones.length > 0
    ? `Run ended. ${game.reason}. Archive stamp unlocked: ${milestones.join(", ")}`
    : `Run ended. ${game.reason}. Score ${snapshot.score}.`;
  if (unlocked.length) announcement.textContent += ` Skin unlocked: ${unlocked.map(skin => skin.name).join(", ")}.`;
  if (!death) announcement.textContent += " Result recorded.";
}

function consumeGameEvents(): void {
  const events = game.drainEvents();
  renderer.consume(events);
  audio.consume(events);
  if (events.some((event) => event.type === "death")) showGameOver(events);
  const milestone = events.find((event) => event.type === "milestone");
  if (milestone?.type === "milestone") announcement.textContent = milestone.label;
  if (events.some((event) => event.type === "piston")) announcement.textContent = "Cyborg piston kick";
  const plow = events.find((event) => event.type === "hazardBreak" && event.combo > 0);
  if (plow?.type === "hazardBreak") announcement.textContent = `Chain times ${plow.combo}. ${plow.points} points`;
  if (events.some((event) => event.type === "overdrive")) announcement.textContent = "Gold Star Patent Overdrive active. All rivals break for five seconds";
  const patentCollected = events.find((event) => event.type === "patentCollected");
  if (patentCollected?.type === "patentCollected") announcement.textContent = patentCollected.added ? "Patent protection " + patentCollected.count + " of " + MAX_PATENTS : "Patent protection already full. +500 points";
  const patentLost = events.find((event) => event.type === "patentLost");
  if (patentLost?.type === "patentLost") announcement.textContent = "Patent lost. " + patentLost.count + " remaining";
}

function updateHud(): void {
  const snapshot = game.snapshot();
  canvas.dataset.material = snapshot.tomato.material;
  canvas.dataset.runState = snapshot.state;
  scoreElement.textContent = snapshot.score.toLocaleString();
  patentElement.textContent = snapshot.patentCount + "/" + MAX_PATENTS;
  const beatBest = runStartBest > 0 && snapshot.score > runStartBest;
  if (snapshot.state === "playing" && beatBest && !newBestNotified) {
    newBestNotified = true;
    newBestBanner.classList.remove("hidden");
    announcement.textContent = "New best score!";
  }
  const liveBest = newBestNotified && (snapshot.state === "playing" || snapshot.state === "paused");
  hudBest.classList.toggle("is-new-best", liveBest);
  const best = liveBest ? snapshot.score : saveData.bestScore;
  bestScoreElement.textContent = best > 0 ? best.toLocaleString() : "—";
  patentMultiplierElement.textContent = "×" + snapshot.scoreMultiplier;
  depthElement.textContent = Math.floor(snapshot.depth / 40) + "D";
  eraStatusElement.textContent = `${snapshot.era.year} · ${snapshot.era.name}`;
  audio.sterilizerWarning(snapshot.tomato.y - snapshot.tomato.radius - snapshot.sterilizerY);
}

function pollGamepad(): void {
  const pads = navigator.getGamepads?.() ?? [];
  const pressed = [...pads].some((pad) => pad?.buttons[0]?.pressed === true || pad?.buttons[1]?.pressed === true);
  if (!pressed) gamepadNeedsRelease = false;
  if (game.state !== "playing" || gamepadNeedsRelease) return;
  if (pressed === gamepadHeld) return;
  gamepadHeld = pressed;
  if (currentSettings().toggleControl && pressed) toggledCore = !toggledCore;
  updateRequestedMaterial();
}

function frame(now: number): void {
  const elapsed = Math.min(0.1, Math.max(0, (now - lastFrameTime) / 1000));
  lastFrameTime = now;
  pollGamepad();

  if (game.state === "playing") {
    accumulator += elapsed;
    while (accumulator >= FIXED_DT) {
      game.tick();
      accumulator -= FIXED_DT;
      consumeGameEvents();
      if (game.state !== "playing") {
        accumulator = 0;
        break;
      }
    }
  } else {
    accumulator = 0;
  }

  updateHud();
  if (game.state !== "paused") renderer.draw(game.snapshot(), elapsed);
  requestAnimationFrame(frame);
}

const nativeGestureAbortController = new AbortController();
const suppressNativeGameplayGesture = (event: Event): void => event.preventDefault();
canvas.addEventListener("contextmenu", suppressNativeGameplayGesture, { signal: nativeGestureAbortController.signal });
canvas.addEventListener("dragstart", suppressNativeGameplayGesture, { signal: nativeGestureAbortController.signal });
window.addEventListener("pagehide", () => {
  persistActiveBest();
  nativeGestureAbortController.abort();
});
newBestBanner.addEventListener("animationend", () => newBestBanner.classList.add("hidden"));
canvas.addEventListener("pointerdown", (event) => {
  event.preventDefault();
  canvas.setPointerCapture(event.pointerId);
  handlePressStart();
});
canvas.addEventListener("pointerup", (event) => {
  event.preventDefault();
  handlePressEnd();
});
canvas.addEventListener("pointercancel", pauseRun);
pauseButton.addEventListener("pointerdown", (event) => event.stopPropagation());
pauseButton.addEventListener("click", pauseRun);

window.addEventListener("keydown", (event) => {
  if (settingsDialog.open) return;
  if (event.code === "Escape" || event.code === "KeyP") {
    if (event.repeat || (game.state !== "playing" && game.state !== "paused")) return;
    event.preventDefault();
    if (game.state === "playing") pauseRun();
    else resumeRun();
    return;
  }
  if ((event.code !== "Space" && event.code !== "Enter") || event.repeat) return;
  if (event.target instanceof HTMLButtonElement || event.target instanceof HTMLInputElement || settingsDialog.open) return;
  event.preventDefault();
  handlePressStart();
});
window.addEventListener("keyup", (event) => {
  if (event.code !== "Space" && event.code !== "Enter") return;
  if (event.target instanceof HTMLButtonElement || event.target instanceof HTMLInputElement || settingsDialog.open) return;
  event.preventDefault();
  handlePressEnd();
});

requiredElement<HTMLButtonElement>("play-button").addEventListener("click", () => {
  void audio.unlock();
  beginRun(MAIN_TIMELINE_SEED, "normal", randomSeed());
});
requiredElement<HTMLButtonElement>("random-button").addEventListener("click", () => {
  void audio.unlock();
  const seed = randomSeed();
  beginRun(seed, "random", seed);
});
requiredElement<HTMLButtonElement>("retry-button").addEventListener("click", () => {
  void audio.unlock();
  beginRun(game.seed, game.snapshot().mode, game.eraSeed);
});
alternateButton.addEventListener("click", () => {
  void audio.unlock();
  if (game.snapshot().mode === "random") beginRun(MAIN_TIMELINE_SEED, "normal", randomSeed());
  else {
    const seed = randomSeed();
    beginRun(seed, "random", seed);
  }
});
requiredElement<HTMLButtonElement>("home-button").addEventListener("click", returnHome);
requiredElement<HTMLButtonElement>("resume-button").addEventListener("click", resumeRun);
requiredElement<HTMLButtonElement>("restart-button").addEventListener("click", () => {
  persistActiveBest();
  void audio.unlock();
  beginRun(game.seed, game.snapshot().mode, game.eraSeed);
});
requiredElement<HTMLButtonElement>("pause-home-button").addEventListener("click", () => {
  persistActiveBest();
  returnHome();
});

function persistActiveBest(): void {
  if (game.state !== "playing" && game.state !== "paused") return;
  const snapshot = game.snapshot();
  recordBest(saveData, snapshot.score, snapshot.depth);
  bestTitle.textContent = saveData.bestScore.toLocaleString();
}

function openSettings(): void {
  settingsPausedGame = game.state === "playing";
  if (settingsPausedGame) pauseRun();
  settingsDialog.showModal();
}

settingsButton.addEventListener("click", openSettings);
requiredElement<HTMLButtonElement>("pause-settings-button").addEventListener("click", openSettings);

settingsDialog.addEventListener("close", () => {
  saveData.settings = {
    skin: saveData.settings.skin,
    muted: muteSetting.checked,
    reducedMotion: motionSetting.checked,
    highContrast: contrastSetting.checked,
    toggleControl: toggleSetting.checked,
    invertHold: invertSetting.checked,
  };
  save(saveData);
  applySettings();
  updateRequestedMaterial();
  if (settingsPausedGame) {
    resumeRun();
  }
  settingsPausedGame = false;
});

document.addEventListener("visibilitychange", () => {
  if (document.hidden) pauseRun();
});
window.addEventListener("blur", pauseRun);

bestTitle.textContent = saveData.bestScore.toLocaleString();
applySettings();
updateNavigation();
renderer.draw(game.snapshot(), 0);
requestAnimationFrame(frame);
